import React, { useEffect, useState } from "react";
import { Row, Col, Form, Image, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Card from "../Card";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import {
  getAcademicYearList,
  addAcademicYear,
  updateAcademicYear,
  deleteAcademicYear,
  updateAcademicYearStatus,
} from "../../services/Academic-Services";

import {
  callToast,
  prepareSiteList,
  showErrorMessages,
} from "../../services/utilityFunctions.js";
import { useGlobalState } from "../../services/hostelConst";
import Select from "react-select";
import { getLocalStorageData } from "../../services/axiosComponent";
import { selectStyle } from "../../services/constant";
import { useSelector, useDispatch } from "react-redux";
import {
  openModal,
  closeModal,
} from "../../services/redux/reducers/modalSlice";

const AddAcademicYearForm = ({ navigate, refresh }) => {
  const modal = useSelector((state) => state.modal);
  const dispatch = useDispatch();

  const [regionOptions, setRegionOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSiteOptionsLoading, setIsSiteOptionsLoading] = useState(false);
  const [siteOptions, setSiteOptions] = useState([]);
  const [isSiteSearchable, setIsSiteSearchable] = useState(true);
  const [isRegionSearchable, setIsRegionSearchable] = useState(true);

  const prepareFormData = (data = null) => {
    return {
      name: data != null ? data.NAME : "",
      description: data != null ? data.DESCRIPTION : "",
      startDate: data != null ? data.START_DATE : "",
      endDate: data != null ? data.END_DATE : "",
      isCurrencyAcademicYear:
        data != null ? data.IS_CURRENT_ACADEMIC_YEAR === "Y" : false,
      siteId: {
        value: data != null ? data["_SITE"] : "",
        label: data != null ? data["SITE"] : "",
      },
      regionId: {
        value: data != null ? data["_REGION"] : "",
        label: data != null ? data["REGION"] : "",
      },
    };
  };

  const [loadingData, setLoadingData] = useState(modal.isEditing);
  const [formData, setFormData] = useState(prepareFormData());

  const getAcademicYearDetailsData = async (academicYearId) => {
    setLoadingData(true);
    try {
      // You may need to implement getAcademicYearDetails service or use getAcademicYearList with filter
      var res = await getAcademicYearList({ id: academicYearId });
      if (res.status == "fail") {
        showErrorMessages({ data: res, navigate });
      } else {
        console.log(res, "get academic year details");
        if (res.data.entityList && res.data.entityList.length > 0) {
          setFormData(prepareFormData(res.data.entityList[0]));
        }
      }
    } catch (error) {
      console.error("Error fetching academic year details:", error);
    }
    setLoadingData(false);
  };

  const handleChange = (field, value) => {
    setErrors({});
    if (field == "regionId") {
      setFormData((prev) => ({ ...prev, siteId: { value: "", label: "" } }));
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateFields = () => {
    let isValid = true;
    let errors = {};

    if (formData.name == "") {
      isValid = false;
      errors.name = "Academic Year Name is required";
    }

    if (formData.description == "") {
      isValid = false;
      errors.description = "Description is required";
    }

    if (formData.startDate == "") {
      isValid = false;
      errors.startDate = "Start Date is required";
    }

    if (formData.endDate == "") {
      isValid = false;
      errors.endDate = "End Date is required";
    }

    if (formData.startDate && formData.endDate) {
      const startDate = dayjs(formData.startDate);
      const endDate = dayjs(formData.endDate);

      if (endDate.isBefore(startDate)) {
        isValid = false;
        errors.endDate = "End Date must be after Start Date";
      }
    }

    if (formData.regionId.value == "") {
      isValid = false;
      errors.region = "Region is required";
    }

    if (formData.siteId.value == "") {
      isValid = false;
      errors.site = "Site is required";
    }

    setErrors(errors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (validateFields()) {
      setLoading(true);

      var submitData = {
        name: formData.name,
        description: formData.description,
        startDate: formData.startDate,
        endDate: formData.endDate,
        isCurrencyAcademicYear: formData.isCurrencyAcademicYear ? "Y" : "N",
        organizationId:
          getLocalStorageData("profileDetails")["_ORGANIZATION_"]._ID,
        regionId: formData.regionId.value,
        siteId: formData.siteId.value,
        id: modal.isEditing ? modal.data._ID : null,
      };

      var res = modal.isEditing
        ? await updateAcademicYear(submitData)
        : await addAcademicYear(submitData);

      if (res.status == "success") {
        callToast(
          "success",
          modal.isEditing ? "Updated" : "Created",
          modal.isEditing
            ? "Academic Year updated successfully"
            : "Academic Year added successfully"
        );

        await refresh({ columnRefresh: false, isLoading: false });
        setLoading(false);
        dispatch(closeModal());
      } else {
        setLoading(false);
        showErrorMessages({
          data: res,
          navigate,
        });
      }
    }
  };

  const getSiteList = async (regionId, query) => {
    setIsSiteOptionsLoading(true);
    var data = await JSON.parse(localStorage.getItem("profileDetails"));
    var list = await prepareSiteList(regionId, navigate, query);

    if (
      list.length == 1 &&
      list[0].value == data._SITE &&
      data["_SITE ACCESS_"] == null
    ) {
      setIsSiteSearchable(false);
      handleChange("siteId", {
        value: list[0].value,
        label: list[0].label,
      });
    }

    setIsSiteOptionsLoading(false);
    if (list.length == 0) {
      setErrors((prev) => ({
        ...prev,
        site: "No site available for this region",
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        site: "",
      }));
    }
    setSiteOptions(list);
  };

  const getRegionList = async () => {
    var data = await JSON.parse(localStorage.getItem("profileDetails"));
    var list = JSON.parse(localStorage.getItem("regionList"));
    if (
      list.length == 1 &&
      list[0].value == data._REGION &&
      data["_REGION_ACCESS_"] == null
    ) {
      setIsRegionSearchable(false);
      handleChange("regionId", {
        value: list[0].value,
        label: list[0].label,
      });
    }
    setRegionOptions(list);
  };

  useEffect(() => {
    getRegionList();
    if (modal.isEditing == true) {
      getAcademicYearDetailsData(modal.data._ID);
    }

    return () => {};
  }, []);

  return (
    <>
      {loadingData ? (
        <center>
          <div class="spinner-border text-primary" role="status"></div>
        </center>
      ) : (
        <section className="login-content">
          <div className="">
            <div className="d-flex">
              <div className="w-100 ps-5 pe-5">
                <form>
                  <>
                    <div className="mb-3">
                      <div className="form-row ">
                        <div class="col">
                          <label className="form-label">
                            Academic Year Name
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Academic Year Name (e.g., 2024-2025)"
                            value={formData.name}
                            onChange={(e) =>
                              handleChange("name", e.target.value)
                            }
                          />
                          {errors.name && (
                            <small className="text-danger">{errors.name}</small>
                          )}
                        </div>
                        <div class="col mt-3">
                          <label className="form-label">Description</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Description"
                            value={formData.description}
                            onChange={(e) =>
                              handleChange("description", e.target.value)
                            }
                          />
                          {errors.description && (
                            <small className="text-danger">
                              {errors.description}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="row">
                        <div className="col">
                          <label className="form-label">Start Date</label>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              value={
                                formData.startDate == ""
                                  ? null
                                  : dayjs(formData.startDate)
                              }
                              onChange={(date) => {
                                handleChange(
                                  "startDate",
                                  date
                                    ? date.format("DD/MM/YYYY").toString()
                                    : ""
                                );
                              }}
                              className="form-control"
                            />
                          </LocalizationProvider>
                          {errors.startDate && (
                            <small className="text-danger">
                              {errors.startDate}
                            </small>
                          )}
                        </div>
                        <div className="col">
                          <label className="form-label">End Date</label>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              value={
                                formData.endDate == ""
                                  ? null
                                  : dayjs(formData.endDate)
                              }
                              onChange={(date) => {
                                handleChange(
                                  "endDate",
                                  date
                                    ? date.format("DD/MM/YYYY").toString()
                                    : ""
                                );
                              }}
                              className="form-control"
                            />
                          </LocalizationProvider>
                          {errors.endDate && (
                            <small className="text-danger">
                              {errors.endDate}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="isCurrencyAcademicYear"
                          checked={formData.isCurrencyAcademicYear}
                          onChange={(e) =>
                            handleChange(
                              "isCurrencyAcademicYear",
                              e.target.checked
                            )
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="isCurrencyAcademicYear"
                        >
                          Set as Current Academic Year
                        </label>
                      </div>
                    </div>

                    <div className="mb-3 ">
                      <label className="form-label">Region </label>

                      <Select
                        styles={selectStyle}
                        options={regionOptions}
                        value={
                          formData.regionId.label == ""
                            ? null
                            : formData.regionId
                        }
                        onChange={(newValue) => {
                          handleChange(
                            "regionId",
                            newValue == null
                              ? { value: "", label: "" }
                              : newValue
                          );

                          if (newValue != null) {
                            getSiteList(newValue.value);
                          }
                        }}
                        onInputChange={(inputValue, { action }) => {}}
                        noOptionsMessage={() => {
                          return !isRegionSearchable
                            ? "Region Access not available"
                            : "No matches found";
                        }}
                        isSearchable={isRegionSearchable}
                        placeholder="Select Region "
                        className="basic-single"
                        classNamePrefix="select"
                        filterOption={(option, inputValue) =>
                          option.label
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                        }
                      />
                      {errors.region && (
                        <small className="text-danger">{errors.region}</small>
                      )}
                    </div>

                    <div className="mb-3 ">
                      <label className="form-label">Site</label>

                      <Select
                        styles={selectStyle}
                        options={siteOptions}
                        value={
                          formData.siteId.label == "" ? null : formData.siteId
                        }
                        components={
                          isSiteOptionsLoading
                            ? {
                                DropdownIndicator,
                              }
                            : null
                        }
                        onChange={(newValue) => {
                          handleChange(
                            "siteId",
                            newValue == null
                              ? { value: "", label: "" }
                              : newValue
                          );
                        }}
                        onInputChange={(inputValue, { action }) => {
                          getSiteList(formData.regionId.value, inputValue);
                        }}
                        noOptionsMessage={() => {
                          return !isSiteSearchable
                            ? "Site Access Not available"
                            : "No matches found";
                        }}
                        isSearchable={isSiteSearchable}
                        placeholder="Select Site "
                        className="basic-single"
                        classNamePrefix="select"
                        filterOption={(option, inputValue) =>
                          option.label
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                        }
                      />
                      {errors.site && (
                        <small className="text-danger">{errors.site}</small>
                      )}
                    </div>
                  </>

                  <div className="d-flex justify-content-end mt-4 gap-2">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? (
                        <div
                          className="spinner-border text-light"
                          role="status"
                        >
                         
                        </div>
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

const DropdownIndicator = (props) => {
  return (
    <div {...props.innerProps}>
      <div
        class="spinner-border text-primary spinner-border-sm m-2"
        role="status"
      ></div>
    </div>
  );
};

export default AddAcademicYearForm;
